import { useState, useRef } from 'react';
import { Camera, X, Check, Upload } from 'lucide-react';
import Swal from 'sweetalert2';
import styles from './ProfileImageUploader.module.css';

const ProfileImageUploader = ({ 
    isOpen, 
    onClose, 
    currentImage, 
    onImageUpdate,
    token 
    }) => {
    const [selectedImage, setSelectedImage] = useState(null);
    const [previewImage, setPreviewImage] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [showCropper, setShowCropper] = useState(false);
    const [originalImage, setOriginalImage] = useState(null);
    const fileInputRef = useRef(null);

    // Función para redimensionar y comprimir imagen a base64
    const resizeImageToBase64 = (file, maxWidth = 200, maxHeight = 200, quality = 0.7) => {
        return new Promise((resolve) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        
        img.onload = () => {
            const size = Math.min(img.width, img.height);
            const x = (img.width - size) / 2;
            const y = (img.height - size) / 2;
            
            canvas.width = maxWidth;
            canvas.height = maxHeight;
            
            ctx.drawImage(img, x, y, size, size, 0, 0, maxWidth, maxHeight);
            
            const base64 = canvas.toDataURL('image/jpeg', quality);
            resolve(base64);
        };
        
        img.src = URL.createObjectURL(file);
        });
    };

    // Manejar selección de archivo
    const handleFileSelect = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        
        // Validar tipo de archivo
        if (!file.type.startsWith('image/')) {
        await Swal.fire({
            icon: 'error',
            title: 'Archivo no válido',
            text: 'Por favor selecciona un archivo de imagen (JPG, PNG, WebP)',
            confirmButtonText: 'Entendido', 
            confirmButtonColor: '#242b57', 
            customClass: { 
                popup: 'popupbonito'
            }
        });
        return;
        }

        // Validar tamaño del archivo (máximo 5MB)
        if (file.size > 5 * 1024 * 1024) {
        await Swal.fire({
            icon: 'error',
            title: 'Archivo muy grande',
            text: 'El archivo es demasiado grande. Máximo 5MB.',
            confirmButtonText: 'Entendido', 
            confirmButtonColor: '#242b57', 
            customClass: { 
                popup: 'popupbonito'
            }
        });
        return;
        }
        
        setIsProcessing(true);
        
        try {
        // Crear preview optimizada
        const optimizedBase64 = await resizeImageToBase64(file);
        setPreviewImage(optimizedBase64);
        setSelectedImage(optimizedBase64);
        
        // Mostrar mensaje de éxito al procesar la imagen
        await Swal.fire({
            icon: 'success',
            title: 'Imagen procesada',
            text: 'La imagen se ha procesado correctamente. Puedes guardar los cambios.',
            timer: 2000,
            showConfirmButton: false,
            toast: true,
            position: 'top-end'
        });
        
        } catch (error) {
        console.error('Error procesando imagen:', error);
        await Swal.fire({
            icon: 'error',
            title: 'Error al procesar',
            text: 'Error al procesar la imagen. Intenta con otro archivo.',
            confirmButtonText: 'Entendido', 
            confirmButtonColor: '#242b57', 
            customClass: { 
                popup: 'popupbonito'
            }
        });
        } finally {
        setIsProcessing(false);
        }
    };

    // Enviar imagen al backend
    const handleSaveImage = async () => {
        if (!selectedImage || !token) return;
        
        setIsProcessing(true);
        
        // Mostrar loading mientras se guarda
        Swal.fire({
        title: 'Guardando imagen...',
        text: 'Por favor espera mientras actualizamos tu imagen de perfil',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        customClass: { 
            popup: 'popupbonito'
        },
        didOpen: () => {
            Swal.showLoading();
        }
        });
        
        try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/perfil/profile-image`, {
            method: 'POST',
            headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
            profileImage: selectedImage
            })
        });
        
        if (response.ok) {
            const data = await response.json();
            
            // Mostrar éxito
            await Swal.fire({
            icon: 'success',
            title: '¡Imagen actualizada!',
            text: 'Tu imagen de perfil se ha actualizado exitosamente',
            confirmButtonText: 'Entendido', 
            confirmButtonColor: '#242b57', 
            customClass: { 
                popup: 'popupbonito'
            },
            timer: 3000,
            });
            
            // Actualizar imagen en el componente padre
            onImageUpdate(selectedImage);
            
            // Cerrar modal
            handleClose();
        } else {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Error al guardar la imagen');
        }
        } catch (error) {
        console.error('Error guardando imagen:', error);
        await Swal.fire({
            icon: 'error',
            title: 'Error al guardar',
            text: `No se pudo guardar la imagen: ${error.message}`,
            confirmButtonText: 'Entendido', 
            confirmButtonColor: '#242b57', 
            customClass: { 
                popup: 'popupbonito'
            }
        });
        } finally {
        setIsProcessing(false);
        }
    };

    // Manejar cierre del modal
    const handleClose = () => {
        setSelectedImage(null);
        setPreviewImage(null);
        setShowCropper(false);
        setOriginalImage(null);
        onClose();
    };

    // Eliminar imagen actual
    const handleRemoveImage = async () => {
        // Confirmar eliminación con SweetAlert
        const result = await Swal.fire({
        title: '¿Eliminar imagen de perfil?',
        text: 'Esta acción no se puede deshacer',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#242b57',
        cancelButtonColor: '#242b57',
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar',
        reverseButtons: true,
        customClass: { 
            popup: 'popupbonito'
        }
        });

        if (!result.isConfirmed) return;

        setIsProcessing(true);
        
        // Mostrar loading
        Swal.fire({
        title: 'Eliminando imagen...',
        text: 'Por favor espera',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
            Swal.showLoading();
        },
        customClass: { 
            popup: 'popupbonito'
        }
        });
        
        try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/perfil/profile-image`, {
            method: 'DELETE',
            headers: {
            'Authorization': `Bearer ${token}`
            }
        });
        
        if (response.ok) {
            await Swal.fire({
            icon: 'success',
            title: 'Imagen eliminada',
            text: 'Tu imagen de perfil ha sido eliminada',
            confirmButtonText: 'Entendido', 
            confirmButtonColor: '#242b57', 
            customClass: { 
                popup: 'popupbonito'
            },
            timer: 2500
            });
            
            onImageUpdate(null);
            handleClose();
        } else {
            throw new Error('Error al eliminar la imagen');
        }
        } catch (error) {
        console.error('Error eliminando imagen:', error);
        await Swal.fire({
            icon: 'error',
            title: 'Error al eliminar',
            text: 'No se pudo eliminar la imagen. Intenta nuevamente.',
            confirmButtonText: 'Entendido', 
            confirmButtonColor: '#242b57', 
            customClass: { 
                popup: 'popupbonito'
            }
        });
        } finally {
        setIsProcessing(false);
        }
    };

    if (!isOpen) return null;

return (
        <div className={styles.modalOverlay} onClick={handleClose}>
        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
            <h3>Cambiar Imagen de Perfil</h3>
            <button onClick={handleClose} className={styles.closeButton}>
                <X size={20} />
            </button>
            </div>

            <div className={styles.modalBody}>
            {/* Preview de imagen actual o nueva */}
            <div className={styles.imagePreviewContainer}>
                <div className={styles.imagePreview}>
                {previewImage || currentImage ? (
                    <img 
                    src={previewImage || currentImage} 
                    alt="Preview" 
                    className={styles.previewImage}
                    />
                ) : (
                    <div className={styles.placeholderImage}>
                    <Camera size={48} />
                    <p>Sin imagen</p>
                    </div>
                )}
                </div>
            </div>

            {/* Botones de acción */}
            <div className={styles.actionButtons}>
                <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isProcessing}
                className={styles.uploadButton}
                >
                <Upload size={16} />
                {previewImage ? 'Cambiar Imagen' : 'Seleccionar Imagen'}
                </button>

                {(currentImage || previewImage) && (
                <button
                    onClick={handleRemoveImage}
                    disabled={isProcessing}
                    className={styles.removeButton}
                >
                    Eliminar Imagen
                </button>
                )}
            </div>

            {/* Input oculto para seleccionar archivo */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className={styles.hiddenInput}
            />

            {/* Información técnica */}
            <div className={styles.infoBox}>
                <ul>
                <li>Formatos soportados: JPG, PNG, WebP</li>
                <li>Tamaño máximo: 5MB</li>
                </ul>
            </div>
            </div>

            <div className={styles.modalFooter}>
            
            {previewImage && (
                <button
                onClick={handleSaveImage}
                disabled={isProcessing}
                className={styles.saveButton}
                >
                {isProcessing ? (
                    <>
                    <div className={styles.spinner}></div>
                    Guardando...
                    </>
                ) : (
                    <>
                    <Check size={16} />
                    Guardar Imagen
                    </>
                )}
                </button>
            )}
            </div>
        </div>
        </div>
    );
    };

export default ProfileImageUploader;